import type { Role } from '../schemas/common.schema.js';

/**
 * THE PERMISSION VOCABULARY.
 *
 * A permission is `<module>.<action>`, and the action is one of a fixed set, so
 * the Roles & access screen can draw a real grid — modules down the side,
 * actions across the top — instead of a flat list nobody can scan. A module key
 * may itself contain dots (`leave.requests`); the action is always the LAST
 * segment.
 *
 * WHO MAY SEE WHICH ROWS is a different question and stays where it was: the
 * per-person `AccessScope` on the employee record (company / direct reports /
 * self). A role says what you may DO; the scope says to WHOM.
 */
export const PERMISSION_ACTIONS = [
  'view',
  'create',
  'edit',
  'delete',
  'approve',
  'export',
  'manage',
] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export const MODULE_GROUPS = [
  'people',
  'time',
  'pay',
  'operations',
  'insights',
  'system',
  'sensitive',
] as const;
export type ModuleGroup = (typeof MODULE_GROUPS)[number];

export interface PermissionDef {
  action: PermissionAction;
  /** Who holds it out of the box. ADMIN is implied everywhere and need not be listed. */
  defaultRoles: readonly Role[];
  /** One line for the tooltip: what ticking this box actually allows. */
  description?: string;
  /**
   * PLATFORM: it changes how the application itself runs or connects, or who may
   * do what — roles & access, the ERP connection, application settings, the
   * system pages, device and field-app registration. Only the Super
   * Administrator holds a platform permission out of the box; the company
   * Administrator, HR and Manager never do (catalog.test.ts holds every default
   * list to that). Ticking one for another role is an explicit grant.
   */
  platform?: boolean;
}

export interface ModuleDef {
  /** `employees`, `leave.requests`, … */
  key: string;
  label: string;
  group: ModuleGroup;
  permissions: readonly PermissionDef[];
  /** Shorthand: every permission of this module is platform. */
  platform?: boolean;
}

/** `leave.requests` + `approve` → `leave.requests.approve`. */
export function permissionKey(module: string, action: PermissionAction): string {
  return `${module}.${action}`;
}
